import React, { useState, useEffect } from "react";
import {
  Dialog,
  TextField,
  Typography,
  Grid,
  Button,
  IconButton,
  Box,
  Link as MuiLink,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import CloseIcon from "@mui/icons-material/Close";
import AuthorizationClient from '../single-page/api/AuthorizationClient.js';
import ornamentOne from '../../assets/images/ornament_login.svg';
import { ROUTES } from "../single-page/constants/routes.js";
import { useModals } from '/hooks/useModals.jsx';
import { formatLicensePlate } from '../helpers/formatLicensePlate.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { useUser } from '../hooks/useUser';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '16px',
    overflow: 'hidden',
    margin: '16px',
    borderTop: '4px solid #51DF94',
    boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
    [theme.breakpoints.up('sm')]: {
      maxWidth: '800px',
      width: '100%'
    }
  }
}));

const inputStyles = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#fff',
    border: '1.68px solid #012F4740',
    borderRadius: '8px',
    transition: 'all 0.2s ease-in-out',
    '& fieldset': {
      border: 'none'
    },
    '&:hover': {
      borderColor: '#6BD3FD'
    },
    '&.Mui-focused': {
      borderColor: '#51DF94'
    }
  },
  '& .MuiInputLabel-root': {
    color: '#666',
    '&.Mui-focused': {
      color: '#012F47'
    }
  },
  '& .MuiInputLabel-outlined': {
    '&.MuiInputLabel-shrink': {
      background: 'white',
      padding: '0 8px',
      marginLeft: '-4px'
    }
  }
};

/** Multicolor Google "G" for Sign-In With Google button (decorative). */
const GoogleIcon = () => (
  <Box
    component="svg"
    viewBox="0 0 48 48"
    width={20}
    height={20}
    aria-hidden="true"
    sx={{ flexShrink: 0, display: 'block' }}
  >
    <path
      fill="#EA4335"
      d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
    />
    <path
      fill="#4285F4"
      d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6C44.53 39.42 46.98 34.01 46.98 24.55z"
    />
    <path
      fill="#FBBC05"
      d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
    />
    <path
      fill="#34A853"
      d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
    />
  </Box>
);

const LoginModal = () => {
  const [formData, setFormData] = useState({ 
    email: "", 
    password: "",
    license_plate: ""
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { 
    loginOpen, 
    handleCloseLogin, 
    handleOpenRegister,
    licensePlate,
    planType,
    handleOpenForgotPassword
  } = useModals();
  const { checkAuth } = useUser();

  useEffect(() => {
    if (licensePlate) {
      setFormData(prev => ({ ...prev, license_plate: licensePlate }));
    }
    if (planType) {
      setFormData(prev => ({ ...prev, plan_type: planType }));
    }
  }, [licensePlate, planType]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});
    
    try {
      const loginData = { ...formData };
      if (licensePlate) loginData.license_plate = licensePlate;
      if (planType) loginData.plan_type = planType;

      const response = await AuthorizationClient.signIn(loginData);
      await checkAuth();
      
      handleCloseLogin();
      
      if (licensePlate) {
        const inspection = response?.data?.inspection;
        if (inspection?.inspection_id) {
          navigate(`${ROUTES.MY_INSPECTIONS}/${inspection.inspection_id}`);
        } else {
          navigate(ROUTES.MY_INSPECTIONS);
        }
      } else {
        if (location.pathname === '/') {
          navigate(ROUTES.HOME);
        }
      }
    } catch (error) {
      console.error('Erro completo:', error);
      setLoading(false);
      setErrors({
        email: "Email ou senha inválidos",
        password: "Email ou senha inválidos"
      });
    }
  };

  const handleRegisterClick = (e) => {
    e.preventDefault();
    handleCloseLogin();
    handleOpenRegister();
  };

  const handleForgotPasswordClick = (e) => {
    e.preventDefault();
    handleCloseLogin();
    handleOpenForgotPassword();
  };

  return (
    <StyledDialog 
      open={loginOpen}
      onClose={handleCloseLogin}
      fullWidth
      maxWidth="md"
    >
      <Grid container>
        <Grid 
          item 
          xs={12} 
          md={5}
          sx={{ 
            backgroundColor: "#012F47",
            backgroundImage: `url(${ornamentOne})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            minHeight: { xs: '120px', md: '500px' }
          }}
        />

        <Grid item xs={12} md={7}>
          <Box sx={{ position: 'relative', p: 3 }}>
            <IconButton
              onClick={handleCloseLogin}
              sx={{ position: 'absolute', right: 8, top: 8, color: 'grey.500' }}
              disabled={loading}
            >
              <CloseIcon />
            </IconButton>

            <Typography variant="h4" sx={{ mb: 3, fontWeight: 600, color: '#012F47' }}>
              Bem-vindo de volta!
            </Typography>

            <Stack spacing={2} sx={{ width: '100%' }}>
              <form
                onSubmit={handleSubmit}
                autoComplete="off"
              >
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label="E-mail"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    sx={inputStyles}
                    required
                    autoComplete="new-email"
                    inputProps={{
                      autoComplete: "new-email"
                    }}
                  />

                  <TextField
                    fullWidth
                    label="Senha"
                    name="password"
                    type="password"
                    value={formData.password}
                    onChange={handleChange}
                    error={!!errors.password}
                    helperText={errors.password}
                    sx={inputStyles}
                    required
                    autoComplete="new-password"
                    inputProps={{
                      autoComplete: "new-password"
                    }}
                  />

                  <Box sx={{ textAlign: 'right' }}>
                    <MuiLink
                      component="span"
                      onClick={handleForgotPasswordClick}
                      sx={{
                        color: '#012F47',
                        textDecoration: 'none',
                        cursor: 'pointer',
                        display: 'inline-block',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Esqueceu sua senha?
                    </MuiLink>
                  </Box>

                  <Button
                    fullWidth
                    type="submit"
                    variant="contained"
                    disabled={loading}
                    sx={{
                      bgcolor: '#4CAF50',
                      py: 1.5,
                      fontSize: '1rem',
                      textTransform: 'none',
                      '&:hover': { bgcolor: '#388E3C' }
                    }}
                  >
                    {loading ? 'Entrando...' : 'Entrar'}
                  </Button>
                </Stack>
              </form>

              {typeof window !== 'undefined' && window.__AUTOVALID_GOOGLE_OAUTH_PATH__ && (
                <Box
                  component="form"
                  method="post"
                  action={window.__AUTOVALID_GOOGLE_OAUTH_PATH__}
                >
                  <input
                    type="hidden"
                    name="authenticity_token"
                    value={typeof document !== 'undefined'
                      ? document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
                      : ''}
                    readOnly
                  />
                  <Button
                    type="submit"
                    fullWidth
                    variant="outlined"
                    disabled={loading}
                    sx={{
                      borderColor: '#012F47',
                      color: '#012F47',
                      py: 1.5,
                      textTransform: 'none',
                      fontWeight: 600,
                      '&:hover': {
                        borderColor: '#012F47',
                        bgcolor: 'rgba(1, 47, 71, 0.04)'
                      }
                    }}
                  >
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: 1.25,
                        width: '100%'
                      }}
                    >
                      <GoogleIcon />
                      Continuar com Google
                    </Box>
                  </Button>
                </Box>
              )}

              <Box sx={{ textAlign: 'center' }}>
                <Typography variant="body2" color="textSecondary">
                  Não tem uma conta?{' '}
                  <Button
                    onClick={handleRegisterClick}
                    disabled={loading}
                    sx={{
                      color: '#012F47',
                      textTransform: 'none',
                      minWidth: 'auto',
                      padding: '0 4px',
                      '&:hover': {
                        backgroundColor: 'transparent',
                        textDecoration: 'underline'
                      }
                    }}
                  >
                    Cadastre-se
                  </Button>
                </Typography>
              </Box>
            </Stack>

            {licensePlate && (
              <Typography 
                variant="body2" 
                sx={{ 
                  mb: 2, 
                  color: 'text.secondary',
                  textAlign: 'center' 
                }}
              >
                Placa: {formatLicensePlate(licensePlate)}
              </Typography>
            )}
          </Box>
        </Grid>
      </Grid>
    </StyledDialog>
  );
};

export default LoginModal;